import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'identitySection',
    'identityLegend',
    'identityHint',
    'practitionerLinkRow',
    'practitionerLink',
    'avatarUpload',
    'avatarSyncedHint'
  ]

  static values = {
    identityLegendDefault: String,
    identityLegendLinked: String,
    practitionerUrlTemplate: String
  }

  connect () {
    this.stashedValues = null
    this.syncFromPractitionerSelect({ prefill: false })
  }

  practitionerChanged () {
    this.syncFromPractitionerSelect({ prefill: true })
  }

  syncFromPractitionerSelect ({ prefill }) {
    const select = this.practitionerSelect
    if (!select) return

    const hasPractitioner = Boolean(select.value)

    if (hasPractitioner) {
      if (!this.stashedValues) this.stashIdentityValues()
      this.setIdentityDisabled(true)
      this.toggleHint(true)
      this.updateLegend(true)
      this.updatePractitionerLink(select.value)
      this.toggleAvatarFields(true)
      if (prefill) this.prefillFromSelectedOption(select)
    } else {
      this.restoreIdentityValues()
      this.stashedValues = null
      this.setIdentityDisabled(false)
      this.toggleHint(false)
      this.updateLegend(false)
      this.updatePractitionerLink(null)
      this.toggleAvatarFields(false)
    }
  }

  get practitionerSelect () {
    return this.element.querySelector('[name$="[practitioner_id]"]')
  }

  get identityInputs () {
    if (!this.hasIdentitySectionTarget) return []

    return Array.from(
      this.identitySectionTarget.querySelectorAll('input[type="text"], input[type="email"], input[type="tel"], input[type="radio"]')
    )
  }

  stashIdentityValues () {
    const values = {
      text: {},
      gender: null
    }

    this.identityInputs.forEach((input) => {
      if (input.type === 'radio') {
        if (input.checked) values.gender = input.value
      } else {
        values.text[input.name] = input.value
      }
    })

    this.stashedValues = values
  }

  restoreIdentityValues () {
    if (!this.stashedValues) return

    this.identityInputs.forEach((input) => {
      if (input.type === 'radio') {
        input.checked = input.value === this.stashedValues.gender
      } else if (Object.prototype.hasOwnProperty.call(this.stashedValues.text, input.name)) {
        input.value = this.stashedValues.text[input.name]
      }
    })
  }

  setIdentityDisabled (disabled) {
    this.identityInputs.forEach((input) => {
      input.disabled = disabled
    })
  }

  toggleHint (visible) {
    if (!this.hasIdentityHintTarget) return

    this.identityHintTarget.classList.toggle('d-none', !visible)
  }

  toggleAvatarFields (linked) {
    if (this.hasAvatarUploadTarget) {
      this.avatarUploadTarget.classList.toggle('d-none', linked)
    }

    if (this.hasAvatarSyncedHintTarget) {
      this.avatarSyncedHintTarget.classList.toggle('d-none', !linked)
    }
  }

  updateLegend (linked) {
    if (!this.hasIdentityLegendTarget) return

    this.identityLegendTarget.textContent = linked
      ? this.identityLegendLinkedValue
      : this.identityLegendDefaultValue
  }

  updatePractitionerLink (practitionerId) {
    if (!this.hasPractitionerLinkRowTarget || !this.hasPractitionerLinkTarget) return

    const visible = Boolean(practitionerId)
    this.practitionerLinkRowTarget.classList.toggle('d-none', !visible)

    if (visible && this.practitionerUrlTemplateValue) {
      this.practitionerLinkTarget.href = this.practitionerUrlTemplateValue.replace('__ID__', practitionerId)
    }
  }

  prefillFromSelectedOption (select) {
    const attributes = this.selectedPractitionerAttributes(select)
    if (!attributes) return

    this.setNamedFieldValue('first_name', attributes.first_name)
    this.setNamedFieldValue('last_name', attributes.last_name)
    this.setNamedFieldValue('email', attributes.email)
    this.setNamedFieldValue('phone', attributes.phone || '')
    this.setGender(attributes.gender)
  }

  selectedPractitionerAttributes (select) {
    const tomSelect = select.tomselect
    if (!tomSelect) return null

    const option = tomSelect.options[select.value]
    return option?.data?.attributes || null
  }

  setNamedFieldValue (attribute, value) {
    const input = this.identitySectionTarget.querySelector(`[name$="[${attribute}]"]`)
    if (!input || input.type === 'radio') return

    input.value = value == null ? '' : value
  }

  setGender (gender) {
    if (!gender) return

    this.identityInputs
      .filter((input) => input.type === 'radio' && input.name.endsWith('[gender]'))
      .forEach((input) => {
        input.checked = input.value === gender
      })
  }
}
